// src/components/authorizations/ActiveCampaignConnectionForm.tsx
//
// ActiveCampaign auth-create flow: API URL + API key + Test connection + name.
// The backend validates the URL (it must be the customer's own
// `<account>.api-us1.com` style host) and the key, and answers a 400 with a
// `detail` saying which of the two is wrong; `apiJson` surfaces it as the
// error message, so the failure toast is the backend's exact diagnosis.
//
// Rendered inline in the setup wizard's modal (a drawer launched from a modal
// sits under the modal's overlay and focus trap, so its fields cannot be
// used), and in a drawer on the Authorizations page.
import { useState } from "react";
import {
  Alert,
  Button,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  testActiveCampaignCredentials,
  createActiveCampaignAuthorization,
  type ActiveCampaignTestResult,
} from "@/api/authorizations";

type Props = {
  /** Called after the connection was created. */
  onCreated: () => void;
  /** Shown as a Cancel button when set. */
  onCancel?: () => void;
};

export function ActiveCampaignConnectionForm({ onCreated, onCancel }: Props) {
  const [name, setName] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ActiveCampaignTestResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleTest = async () => {
    if (!apiUrl.trim() || !apiKey.trim()) {
      notifications.show({ color: "red", message: "API URL and API key are required" });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testActiveCampaignCredentials({
        apiUrl: apiUrl.trim(),
        apiKey: apiKey.trim(),
      });
      setTestResult(result);
      if (!name.trim() && result.account) {
        setName(result.account);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Connection test failed";
      notifications.show({ color: "red", title: "Test failed", message });
    } finally {
      setTesting(false);
    }
  };

  const canSubmit =
    !!testResult && !!name.trim() && !!apiUrl.trim() && !!apiKey.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await createActiveCampaignAuthorization({
        name: name.trim(),
        apiUrl: apiUrl.trim(),
        apiKey: apiKey.trim(),
      });
      notifications.show({
        color: "green",
        message: "ActiveCampaign authorization created",
      });
      onCreated();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to create authorization";
      notifications.show({ color: "red", title: "Error", message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        We recommend creating a dedicated API user in ActiveCampaign for this
        connection, so you can revoke it without affecting anyone else. Your
        API URL and API key are shown under Settings &rsaquo; Developer.
      </Text>

      <TextInput
        label="API URL"
        placeholder="https://youraccount.api-us1.com"
        value={apiUrl}
        onChange={(e) => {
          setApiUrl(e.currentTarget.value);
          setTestResult(null);
        }}
        required
      />

      <PasswordInput
        label="API key"
        placeholder="Paste your ActiveCampaign API key"
        value={apiKey}
        onChange={(e) => {
          setApiKey(e.currentTarget.value);
          setTestResult(null);
        }}
        required
      />

      <Button
        variant="light"
        onClick={handleTest}
        loading={testing}
        disabled={!apiUrl.trim() || !apiKey.trim()}
      >
        Test connection
      </Button>

      {testResult && (
        <Alert
          icon={<IconCheck size={16} />}
          color="green"
          variant="light"
        >
          Connected to <strong>{testResult.account}</strong>.
        </Alert>
      )}

      <TextInput
        label="Authorization name"
        placeholder="My ActiveCampaign account"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        required
      />

      <Group justify="flex-end" mt="md">
        {onCancel && (
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
        >
          Create authorization
        </Button>
      </Group>
    </Stack>
  );
}
